import { useRoute, useRouter } from 'vue-router'
import { createLogger } from '@/utils/logger'

const logger = createLogger('RouterNavigation')

/**
 * Composable for safe programmatic navigation
 * Prevents race conditions and duplicate navigation attempts
 */
export function useRouterNavigation() {
  const route = useRoute()
  const router = useRouter()

  /**
   * Navigate to a path with duplicate prevention and error handling
   * @param path - Target route path
   * @returns Promise that resolves when navigation completes or is prevented
   */
  const navigateTo = async (path: string): Promise<boolean> => {
    // Prevent duplicate navigation to the same route
    if (route.path === path) {
      logger.log('Already on target route, skipping navigation', { path })
      return false
    }

    try {
      await router.push(path)
      logger.log('Navigation successful', { from: route.path, to: path })
      return true
    } catch (err) {
      // Navigation failures are common (e.g., navigation guards, duplicate navigation)
      // Log but don't throw - let caller decide how to handle
      logger.warn('Navigation failed', {
        from: route.path,
        to: path,
        error: err instanceof Error ? err.message : String(err),
      })
      return false
    }
  }

  return {
    navigateTo,
  }
}
